import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Loader2 } from 'lucide-react';

interface SubscriptionProtectedRouteProps {
  children: ReactNode;
}

export function SubscriptionProtectedRoute({ children }: SubscriptionProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { hasAccess, isExpired, isBlocked, loading: subLoading, isSuperAdmin } = useSubscription();
  const { isProfileComplete, loading: profileLoading } = useUserProfile();
  const location = useLocation();

  // Show loading while checking auth and subscription
  if (authLoading || subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verificando acesso...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Check profile completeness
  if (profileLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verificando perfil...</p>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if profile is incomplete
  if (!isProfileComplete && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // Super admin always has access
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Subscription expired or blocked - redirect to expired page
  if (isExpired || isBlocked || !hasAccess) {
    return <Navigate to="/assinatura-expirada" replace />;
  }

  // Has valid access - render children
  return <>{children}</>;
}
